import { UploadFileProps } from '@anm/components/InputFile';
import UploadImageWithPreview from '@anm/components/UploadImageWithPreview';
import Button from '@anm/components/buttons/Button';
import ButtonLoader from '@anm/components/buttons/ButtonLoader';
import Checkbox from '@anm/components/form/Checkbox';
import Input from '@anm/components/form/Input';
import Select from '@anm/components/select/Select';
import getCaps from '@anm/data/streaming/destinationsCaps';
import useSwitchState from '@anm/hooks/useSwitchState';
import useTextAreaOrInputState from '@anm/hooks/useTextAreaOrInputState';
import isUndefined from 'lodash/fp/isUndefined';
import { useCallback, useState, FC } from 'react';

import { FieldCounter, Label } from '../../../StreamModalWrapper';
import DestinationInfo from '../../../destination_management/DestinationList/components/DestinationInfo';
import { EditStreamDestinationStepProps } from '../../../types';
import { FB_GROUP_HINT } from '../../hooks/useSaveDestinationsMetaParams';
import useValidateTextField from '../../hooks/useValidateTextField';
import Header from '../Header';
import { DescriptionField } from '../StreamDetailsStep/Wrapper';

import StreamRotation from './StreamRotation';
import TwitchCategory from './TwitchCategory';
import { ButtonsContainer, Hint, InfoWrapper, InstructionRow, PreviewRow } from './Wrapper';
import { useChangeData, useDestinationPrivacy, usePreviewUpdate, useStreamRotation } from './hooks';

const EditStreamDestination: FC<EditStreamDestinationStepProps> = ({
  isEdit,
  isPending,
  previewSrc,
  destination,
  savedPreview,
  canShowPreview,
  isFbInstruction,
  name: defaultName,
  privacy: defaultPrivacy,
  rotation: defaultRotation,
  description: defaultDescription,
  onBack,
  onPreviewSelect,
  onOpenDiscardModal,
  onSubmitDestinationMeta
}) => {
  const [name, onNameChange] = useTextAreaOrInputState(defaultName);
  const [description, onDescriptionChange] = useTextAreaOrInputState(defaultDescription);
  const [privacy, privacyOptions, setPrivacy] = useDestinationPrivacy(destination, defaultPrivacy);
  const [rotate, setRotate] = useStreamRotation(defaultRotation);

  const [isUploaded, isFieldsChanged] = useChangeData({
    name,
    defaultName,
    description,
    defaultDescription,
    defaultPrivacy,
    privacy: privacy.value
  });

  const {
    canCustomizeVisibility,
    canCustomizeCategories,
    canCustomizeFbInstruction,
    canCustomizeStreamRotation,
    canCustomizeDescription,
    canCustomizeThumbnail
  } = getCaps(destination.network, destination.kind);

  const titleMaxLength = getCaps(destination.network, destination.kind).titleMaxLength;

  const privacyValue = privacy.value;

  const [isNameError, validateName, handleNameChange] = useValidateTextField({
    text: name,
    titleMaxLength,
    onTextChange: onNameChange
  });
  const streamDescriptionLength = getCaps(destination.network, destination.kind).descriptionMaxLength;
  const [isDescriptionError, validateDescription, handleDescriptionChange] = useValidateTextField({
    text: description,
    titleMaxLength: streamDescriptionLength,
    onTextChange: onDescriptionChange
  });

  const [src, hasUploadError] = usePreviewUpdate({ isUploaded: isUploaded.current, savedPreview, previewSrc });
  const defaultIsInstruction =
    description.includes(FB_GROUP_HINT) || isFbInstruction || (!isEdit && isUndefined(isFbInstruction));
  const [showFbInstruction, , , toggleFbInstruction] = useSwitchState(defaultIsInstruction);

  const [twitchCategoryId, setTwitchCategoryId] = useState<string>();

  const handleUpdateDestination = () => {
    const isValidName = validateName({ titleMaxLength });
    const isValidDescription = validateDescription();

    if (!isValidName || !isValidDescription || hasUploadError || isPending) return;

    onSubmitDestinationMeta({
      title: name,
      preview: previewSrc || savedPreview,
      description: description || null,
      destinationIds: [destination.id],
      isSamePreview: savedPreview === previewSrc,
      ...(twitchCategoryId && { categoryId: twitchCategoryId }),
      ...(canCustomizeFbInstruction && { isFbInstruction: showFbInstruction }),
      ...(canCustomizeVisibility && { privacy: privacyValue }),
      ...(canCustomizeStreamRotation && rotate && { transform: rotate })
    });
    onBack();
  };

  const handleUploadImage = useCallback(
    (p: UploadFileProps[]) => {
      isUploaded.current = true;
      onPreviewSelect({ ...p[0], isEditDestination: true });
    },
    [onPreviewSelect]
  );

  const max_description_length = getCaps(destination.network, destination.kind).descriptionMaxLength;

  return (
    <>
      <Header
        title="Customize the destination"
        onBack={isUploaded.current || isFieldsChanged ? onOpenDiscardModal : onBack}
      />
      <InfoWrapper>
        <DestinationInfo {...destination} />
      </InfoWrapper>
      <Label>
        <FieldCounter isMax={name.length >= titleMaxLength}>
          {name.length} / {titleMaxLength}
        </FieldCounter>
        <span>Event title</span>
        <Input variant="simple-small" value={name} hasError={isNameError} onChange={handleNameChange} />
      </Label>

      {canCustomizeDescription && (
        <Label>
          <FieldCounter isMax={description.length >= max_description_length}>
            {description.length} / {max_description_length}
          </FieldCounter>
          <span>Description</span>
          <DescriptionField
            variant="simple"
            hasError={isDescriptionError}
            onChange={handleDescriptionChange}
            placeholder="Add a description"
            value={description.replace(FB_GROUP_HINT, '').replace(new RegExp('[\n]+$'), '')}
          />
        </Label>
      )}

      {canCustomizeVisibility && (
        <Label>
          <span>Visibility</span>
          <Select name="visibility" value={privacy} options={privacyOptions} onChange={setPrivacy} />
        </Label>
      )}

      {canCustomizeCategories && (
        <TwitchCategory selectedId={twitchCategoryId} onCategorySelect={setTwitchCategoryId} />
      )}

      {canShowPreview && canCustomizeThumbnail && (
        <PreviewRow>
          <UploadImageWithPreview
            title={name}
            previewSrc={src}
            isPending={isPending}
            onImageUpload={handleUploadImage}
          />
          <Hint>Add a custom thumbnail for this social media announcement post</Hint>
        </PreviewRow>
      )}

      {canCustomizeFbInstruction && (
        <InstructionRow>
          <Checkbox checked={showFbInstruction} onChange={toggleFbInstruction} />
          <span>Add instruction on how to allow Facebook to see my name in comments.</span>
        </InstructionRow>
      )}

      {canCustomizeStreamRotation && <StreamRotation {...{ rotate }} onChange={setRotate} />}

      <ButtonsContainer>
        <Button variant="silver-border" size="large" onClick={onBack}>
          Discard changes
        </Button>
        <ButtonLoader isPending={isPending} variant="light-blue" size="large" onClick={handleUpdateDestination}>
          {isPending ? 'Uploading...' : 'Save'}
        </ButtonLoader>
      </ButtonsContainer>
    </>
  );
};

export default EditStreamDestination;
